import { trackButtonClick } from '@/posthog_tracking';
import { isNavigationFailure } from 'vue-router';

export default {
    methods: {
        /**
         * Navigate to a route with automatic PostHog tracking.
         * Tracks current URL, button title (from button text), and location (from current page).
         *
         * @param {Object|string} target - Route object {name, params, query} or route name/path string
         * @param {boolean|Object} isLinkOrOptions - If boolean: whether to open in new tab.
         *                                           If object: tracking options {title, location, isLink, skipTracking}
         */
        navigateTo(target, isLinkOrOptions = false) {
            let name = '';
            let params = null;
            let query = null;
            if (typeof target === 'object') {
                name = target.name;
                params = target.params;
                query = target.query;
            } else {
                name = target;
            }

            // Handle both old boolean signature and new options object
            let isLink = false;
            let trackingTitle = null;
            let trackingLocation = null;
            let skipTracking = false;

            if (typeof isLinkOrOptions === 'boolean') {
                isLink = isLinkOrOptions;
            } else if (typeof isLinkOrOptions === 'object' && isLinkOrOptions !== null) {
                isLink = isLinkOrOptions.isLink || false;
                trackingTitle = isLinkOrOptions.title || null;
                trackingLocation = isLinkOrOptions.location || null;
                skipTracking = isLinkOrOptions.skipTracking || false;
            }

            // Resolve the target URL path for tracking
            let targetUrl = null;
            if (isLink) {
                // External link - use the full URL
                targetUrl = name;
            } else {
                // Internal navigation - resolve the path
                try {
                    let routeData;
                    if (typeof target === 'string' && target.startsWith('/')) {
                        routeData = { path: target };
                        if (query !== null) {
                            routeData = { ...routeData, query };
                        }
                    } else {
                        routeData = { name };
                        if (params !== null) {
                            routeData = { ...routeData, params };
                        }
                        if (query !== null) {
                            routeData = { ...routeData, query };
                        }
                    }
                    const resolved = this.$router.resolve(routeData);
                    targetUrl = resolved.href || resolved.route?.fullPath || null;
                } catch (e) {
                    // If resolution fails, fall back to null
                    targetUrl = null;
                }
            }

            // Auto-determine location from current route if not provided
            if (!trackingLocation && this.$route) {
                // Convert route name like 'calculator-solar' to 'Calculator Solar'
                const routeName = this.$route.name || '';
                trackingLocation = routeName
                    .split('-')
                    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                    .join(' ');
            }

            // Auto-extract title from the event target (button text) if available
            if (!trackingTitle) {
                try {
                    // Try to get text from the active element (the clicked button)
                    const activeEl = document.activeElement;
                    if (activeEl && (activeEl.tagName === 'BUTTON' || activeEl.closest('button'))) {
                        const btn = activeEl.tagName === 'BUTTON' ? activeEl : activeEl.closest('button');
                        trackingTitle = btn?.innerText?.trim() || null;
                    }
                } catch (e) {
                    // Ignore errors in title extraction
                }
            }

            // Always track navigation unless explicitly skipped or already tracked by VButton
            try {
                const alreadyTrackedByVButton = window.__vbuttonTracked === true;
                if (!skipTracking && !alreadyTrackedByVButton) {
                    trackButtonClick({
                        title: trackingTitle,
                        location: trackingLocation,
                        targetUrl: targetUrl,
                    });
                }
            } catch (e) {
                // Silent failure - tracking should never break user flow
            }

            if (isLink) {
                window.open(name, '_blank');
            } else {
                let data;
                // Check if target is a path (starts with /) or route name
                if (typeof target === 'string' && target.startsWith('/')) {
                    data = { path: target };
                    if (query !== null) {
                        data = { ...data, query };
                    }
                } else {
                    data = { name };
                    if (params !== null) {
                        data = { ...data, params };
                    }
                    if (query !== null) {
                        data = { ...data, query };
                    }
                }
                this.$router.push(data).catch(error => {
                    if (error.name === 'NavigationDuplicated') {
                        window.location.reload();
                    } else if (isNavigationFailure(error)) {
                        // Intentional navigation failure (redirect, cancel, abort) by a guard — not an error
                    } else {
                        throw error;
                    }
                });
            }
        },
    },
};
